<!-- Hero Section -->
<section class="cloud-infrastructure-hero relative text-white overflow-hidden py-20 md:py-32">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-blue-400 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-indigo-400 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-blue-300">
            <i class="bx bx-cloud mr-2"></i>Cloud Foundation
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          Cloud Infrastructure <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Services</span>
        </h1>
        
        <p class="text-xl text-blue-100 mb-8 max-w-2xl mx-auto lg:mx-0">
          Build scalable, secure, and high-performance cloud infrastructure with our expert design and implementation services. From virtual networks to storage solutions, we create the foundation for your digital transformation.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Us
          </a>
          <a routerLink="/contact" class="bg-transparent border-2 border-cyan-400 text-cyan-300 hover:bg-cyan-400/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-analyse mr-2"></i>
            Infrastructure Assessment
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto lg:mx-0">
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">High</div>
            <div class="text-sm text-blue-200">Availability</div>
          </div>
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">Auto</div>
            <div class="text-sm text-blue-200">Scaling</div>
          </div>
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">24/7</div>
            <div class="text-sm text-blue-200">Support</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/shape/hosting-solution-one.png" alt="Cloud Infrastructure Services" class="w-full h-auto object-cover">
        </div>
        
        <div class="absolute -top-6 -left-6 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-network-chart text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Virtual</div>
              <div class="text-xs">Networks</div>
            </div>
          </div>
        </div>
        
        <div class="absolute -bottom-6 -right-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-shield-quarter text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Security</div>
              <div class="text-xs">Layers</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Tech Stack Section -->
<section class="py-12 bg-slate-950 border-b border-slate-800/60">
  <div class="container mx-auto px-4">
    <div class="text-center mb-10">
      <h3 class="text-xl font-bold text-slate-300 uppercase tracking-wider">Powered by Industry Leaders</h3>
    </div>
    <div class="flex flex-wrap justify-center gap-10 md:gap-16 items-center grayscale hover:grayscale-0 transition-all duration-500 opacity-70 hover:opacity-100">
      <div class="flex flex-col items-center gap-2 group">
        <i class="bx bxl-aws text-5xl group-hover:text-[#FF9900] transition-colors"></i>
        <span class="text-sm font-semibold text-slate-400 group-hover:text-white">AWS</span>
      </div>
      <div class="flex flex-col items-center gap-2 group">
        <i class="bx bxl-microsoft text-5xl group-hover:text-[#00A4EF] transition-colors"></i>
        <span class="text-sm font-semibold text-slate-400 group-hover:text-white">Azure</span>
      </div>
      <div class="flex flex-col items-center gap-2 group">
        <i class="bx bxl-google-cloud text-5xl group-hover:text-[#4285F4] transition-colors"></i>
        <span class="text-sm font-semibold text-slate-400 group-hover:text-white">Google Cloud</span>
      </div>
      <div class="flex flex-col items-center gap-2 group">
        <i class="bx bxl-kubernetes text-5xl group-hover:text-[#326CE5] transition-colors"></i>
        <span class="text-sm font-semibold text-slate-400 group-hover:text-white">Kubernetes</span>
      </div>
      <div class="flex flex-col items-center gap-2 group">
        <i class="bx bxl-docker text-5xl group-hover:text-[#2496ED] transition-colors"></i>
        <span class="text-sm font-semibold text-slate-400 group-hover:text-white">Docker</span>
      </div>
    </div>
  </div>
</section>

<!-- 4-Phase Infrastructure Process Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-indigo-500/10 border border-indigo-500/40 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Infrastructure Design
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        4-Phase <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-500">Infrastructure Process</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Our structured approach to cloud infrastructure design ensures optimal performance, security, and scalability through careful planning and implementation.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <!-- Phase 1: Assessment -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-cyan-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
            <span class="text-2xl font-bold text-white">1</span>
          </div>
          <span class="bg-cyan-100 text-cyan-800 text-xs font-bold px-3 py-1 rounded-full">Assessment</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Requirements & Assessment</h3>
        <p class="text-slate-300 mb-4">
          Comprehensive evaluation of business requirements, performance needs, and security constraints to create the optimal infrastructure design.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-analyse text-cyan-600 mr-2"></i>
            <span class="text-sm">Workload analysis</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-tachometer text-cyan-600 mr-2"></i>
            <span class="text-sm">Performance requirements</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-lock text-cyan-600 mr-2"></i>
            <span class="text-sm">Security constraints</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-money text-cyan-600 mr-2"></i>
            <span class="text-sm">Cost optimization</span>
          </li>
        </ul>
      </div>

      <!-- Phase 2: Design -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-blue-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
            <span class="text-2xl font-bold text-white">2</span>
          </div>
          <span class="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">Design</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Architecture & Design</h3>
        <p class="text-slate-300 mb-4">
          Custom infrastructure architecture with high availability, disaster recovery, and scalability built-in from the ground up.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-network-chart text-cyan-600 mr-2"></i>
            <span class="text-sm">Network topology</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-hdd text-cyan-600 mr-2"></i>
            <span class="text-sm">Storage architecture</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-shield-quarter text-cyan-600 mr-2"></i>
            <span class="text-sm">Security framework</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-expand text-cyan-600 mr-2"></i>
            <span class="text-sm">Scalability planning</span>
          </li>
        </ul>
      </div>

      <!-- Phase 3: Implementation -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-indigo-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <span class="text-2xl font-bold text-white">3</span>
          </div>
          <span class="bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1 rounded-full">Implementation</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Deployment & Setup</h3>
        <p class="text-slate-300 mb-4">
          Careful implementation of the infrastructure design with continuous monitoring and validation throughout the deployment process.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-server text-cyan-600 mr-2"></i>
            <span class="text-sm">Resource provisioning</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-wifi text-cyan-600 mr-2"></i>
            <span class="text-sm">Network configuration</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-shield-quarter text-cyan-600 mr-2"></i>
            <span class="text-sm">Security implementation</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-test-tube text-cyan-600 mr-2"></i>
            <span class="text-sm">Testing & validation</span>
          </li>
        </ul>
      </div>

      <!-- Phase 4: Optimization -->
      <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-purple-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center">
            <span class="text-2xl font-bold text-white">4</span>
          </div>
          <span class="bg-purple-100 text-purple-800 text-xs font-bold px-3 py-1 rounded-full">Optimization</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Monitoring & Optimization</h3>
        <p class="text-slate-300 mb-4">
          Ongoing performance monitoring, cost optimization, and security hardening to maximize the benefits of your infrastructure.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-tachometer text-cyan-600 mr-2"></i>
            <span class="text-sm">Performance tuning</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-money text-cyan-600 mr-2"></i>
            <span class="text-sm">Cost optimization</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-shield-quarter text-cyan-600 mr-2"></i>
            <span class="text-sm">Security hardening</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-support text-cyan-600 mr-2"></i>
            <span class="text-sm">Ongoing support</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</section>

<!-- Cloud Infrastructure Services Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-indigo-500/10 border border-indigo-500/40 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Infrastructure Solutions
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Cloud Infrastructure <span class="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-blue-500">Services</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Comprehensive infrastructure services for all your cloud computing needs with expert guidance and support for optimal performance.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Virtual Networking -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-blue-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-network-chart text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-blue-500/20 text-blue-200 text-xs font-bold px-3 py-1 rounded-full border border-blue-500/40">Core</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Virtual Networking</h3>
        <p class="text-slate-300 mb-4">
          Secure and scalable virtual network infrastructure with subnets, routing, and connectivity solutions for your cloud environment.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">VPC design & setup</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Subnet configuration</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Route table management</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Network security groups</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">VPN connectivity</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Load balancer setup</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Setup Time:</div>
            <div class="font-semibold text-white">2-3 days</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Availability:</div>
            <div class="font-semibold text-white">99.95%</div>
          </div>
        </div>
      </div>

      <!-- Storage Solutions -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-cyan-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-hdd text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-cyan-500/20 text-cyan-200 text-xs font-bold px-3 py-1 rounded-full border border-cyan-500/40">Storage</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Storage Solutions</h3>
        <p class="text-slate-300 mb-4">
          High-performance storage solutions with block, file, and object storage options optimized for different workloads and use cases.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Block storage (SSD/NVMe)</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">File storage solutions</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Object storage (S3 compatible)</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Storage encryption</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Backup & replication</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Performance optimization</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">IOPS:</div>
            <div class="font-semibold text-white">Up to 100K</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Latency:</div>
            <div class="font-semibold text-white">&lt; 1ms</div>
          </div>
        </div>
      </div>

      <!-- Security Infrastructure -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-purple-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-shield-quarter text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-purple-500/20 text-purple-200 text-xs font-bold px-3 py-1 rounded-full border border-purple-500/40">Security</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Security Infrastructure</h3>
        <p class="text-slate-300 mb-4">
          Comprehensive security infrastructure with firewalls, intrusion detection, and compliance frameworks for enterprise-grade protection.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Network firewalls</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Intrusion detection</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">DDoS protection</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Security monitoring</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Compliance frameworks</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Data encryption</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Protection:</div>
            <div class="font-semibold text-white">Multi-layered</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Monitoring:</div>
            <div class="font-semibold text-white">24/7</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Why Choose Us Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-blue-500/10 border border-blue-500/40 text-blue-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Why Choose Us
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        The <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-500">IIS Advantage</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        We deliver enterprise-grade cloud infrastructure solutions that drive business growth and operational efficiency.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Benefit 1 -->
      <div class="p-6 rounded-2xl bg-slate-900/70 border border-blue-500/40 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group">
        <div class="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
          <i class="bx bx-medal text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Certified Expertise</h3>
        <p class="text-slate-300">
          Our team consists of certified architects and engineers across AWS, Azure, and Google Cloud platforms.
        </p>
      </div>

      <!-- Benefit 2 -->
      <div class="p-6 rounded-2xl bg-slate-900/70 border border-indigo-500/40 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group">
        <div class="w-14 h-14 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
          <i class="bx bx-trending-up text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Performance Optimized</h3>
        <p class="text-slate-300">
          Infrastructure designed for maximum speed and low latency to ensure the best user experience.
        </p>
      </div>

      <!-- Benefit 3 -->
      <div class="p-6 rounded-2xl bg-slate-900/70 border border-cyan-500/40 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group">
        <div class="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
          <i class="bx bx-shield-alt-2 text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Security First</h3>
        <p class="text-slate-300">
          Security is baked into every layer of our infrastructure design, not added as an afterthought.
        </p>
      </div>

      <!-- Benefit 4 -->
      <div class="p-6 rounded-2xl bg-slate-900/70 border border-purple-500/40 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group">
        <div class="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
          <i class="bx bx-wallet text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Cost Efficient</h3>
        <p class="text-slate-300">
          We optimize resource allocation to reduce waste and lower your monthly cloud infrastructure costs.
        </p>
      </div>

      <!-- Benefit 5 -->
      <div class="p-6 rounded-2xl bg-slate-900/70 border border-emerald-500/40 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group">
        <div class="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
          <i class="bx bx-support text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">24/7 Support</h3>
        <p class="text-slate-300">
          Round-the-clock monitoring and support to ensure your infrastructure is always up and running.
        </p>
      </div>

      <!-- Benefit 6 -->
      <div class="p-6 rounded-2xl bg-slate-900/70 border border-amber-500/40 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group">
        <div class="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300">
          <i class="bx bx-rocket text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Rapid Deployment</h3>
        <p class="text-slate-300">
          Automated Infrastructure as Code (IaC) allows us to provision and update environments in minutes.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- Use Cases Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="flex flex-col md:flex-row justify-between items-end mb-12">
      <div class="max-w-2xl">
        <div class="inline-block bg-indigo-500/10 border border-indigo-500/40 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
          Industry Solutions
        </div>
        <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
          Tailored for <span class="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500">Every Industry</span>
        </h2>
        <p class="text-xl text-slate-300">
          Specialized infrastructure patterns designed for specific industry requirements.
        </p>
      </div>
      <a routerLink="/portfolio" class="hidden md:inline-flex items-center font-semibold text-indigo-600 hover:text-indigo-800 transition-colors mt-4 md:mt-0">
        View Success Stories <i class="bx bx-right-arrow-alt ml-1 text-xl"></i>
      </a>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
      <!-- Use Case 1: E-commerce -->
      <div class="bg-slate-900/70 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group border border-slate-700/60">
        <div class="h-48 bg-gradient-to-r from-blue-600 to-indigo-600 relative overflow-hidden">
           <div class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
           <div class="absolute bottom-0 left-0 p-6 text-white">
             <i class="bx bx-cart text-4xl mb-2"></i>
             <h3 class="text-2xl font-bold">E-commerce</h3>
           </div>
        </div>
        <div class="p-6">
          <p class="text-slate-300 mb-4">
            High-concurrency infrastructure capable of handling traffic spikes during sales events while maintaining sub-second page loads.
          </p>
          <ul class="space-y-2">
            <li class="flex items-center text-sm text-slate-200">
              <i class="bx bx-check text-green-500 mr-2"></i> Auto-scaling clusters
            </li>
            <li class="flex items-center text-sm text-slate-200">
              <i class="bx bx-check text-green-500 mr-2"></i> Global CDN integration
            </li>
            <li class="flex items-center text-sm text-slate-200">
              <i class="bx bx-check text-green-500 mr-2"></i> PCI-DSS compliance
            </li>
          </ul>
        </div>
      </div>

      <!-- Use Case 2: FinTech -->
      <div class="bg-slate-900/70 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group border border-slate-700/60">
        <div class="h-48 bg-gradient-to-r from-indigo-600 to-purple-600 relative overflow-hidden">
           <div class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
           <div class="absolute bottom-0 left-0 p-6 text-white">
             <i class="bx bx-line-chart text-4xl mb-2"></i>
             <h3 class="text-2xl font-bold">FinTech</h3>
           </div>
        </div>
        <div class="p-6">
          <p class="text-slate-300 mb-4">
            Ultra-secure, compliant infrastructure environments designed for real-time transaction processing and data protection.
          </p>
          <ul class="space-y-2">
            <li class="flex items-center text-sm text-slate-200">
              <i class="bx bx-check text-green-500 mr-2"></i> End-to-end encryption
            </li>
            <li class="flex items-center text-sm text-slate-200">
              <i class="bx bx-check text-green-500 mr-2"></i> Regulatory compliance
            </li>
            <li class="flex items-center text-sm text-slate-200">
              <i class="bx bx-check text-green-500 mr-2"></i> Micro-segmentation
            </li>
          </ul>
        </div>
      </div>

      <!-- Use Case 3: Healthcare -->
      <div class="bg-slate-900/70 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group border border-slate-700/60">
        <div class="h-48 bg-gradient-to-r from-purple-600 to-pink-600 relative overflow-hidden">
           <div class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
           <div class="absolute bottom-0 left-0 p-6 text-white">
             <i class="bx bx-pulse text-4xl mb-2"></i>
             <h3 class="text-2xl font-bold">Healthcare</h3>
           </div>
        </div>
        <div class="p-6">
          <p class="text-slate-300 mb-4">
            HIPAA-compliant infrastructure ensuring patient data privacy while enabling interoperability and data analysis.
          </p>
          <ul class="space-y-2">
            <li class="flex items-center text-sm text-slate-200">
              <i class="bx bx-check text-green-500 mr-2"></i> HIPAA compliance
            </li>
            <li class="flex items-center text-sm text-slate-200">
              <i class="bx bx-check text-green-500 mr-2"></i> Secure data lakes
            </li>
            <li class="flex items-center text-sm text-slate-200">
              <i class="bx bx-check text-green-500 mr-2"></i> High availability
            </li>
          </ul>
        </div>
      </div>
    </div>
    
    <div class="mt-10 text-center md:hidden">
      <a routerLink="/portfolio" class="inline-flex items-center font-semibold text-indigo-600 hover:text-indigo-800 transition-colors">
        View Success Stories <i class="bx bx-right-arrow-alt ml-1 text-xl"></i>
      </a>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-cyan-600 via-blue-700 to-indigo-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-cyan-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="text-center max-w-4xl mx-auto">
      <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
        Ready to Build Your Cloud Infrastructure?
      </h2>
      <p class="text-xl text-blue-100 mb-10">
        Transform your business with our expert cloud infrastructure services. Start building your scalable, secure, and high-performance cloud environment today.
      </p>
      
      <div class="flex flex-col sm:flex-row gap-4 justify-center mb-12">
        <a routerLink="/contact" class="bg-slate-800/30 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/30 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl inline-flex items-center justify-center">
          <i class="bx bx-cloud mr-2 text-xl"></i>
          <span class="text-lg">Contact Us for Details</span>
        </a>
        <a routerLink="/contact" class="bg-transparent border-2 border-slate-700/50 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/20 transition-all duration-300 inline-flex items-center justify-center">
          <i class="bx bx-support mr-2 text-xl"></i>
          <span class="text-lg">Talk to Infrastructure Expert</span>
        </a>
      </div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-700/40 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">99.9%</div>
          <div class="text-blue-200 text-sm">Uptime SLA</div>
        </div>
        <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-700/40 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">24/7</div>
          <div class="text-blue-200 text-sm">Support</div>
        </div>
        <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-700/40 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">Scalable</div>
          <div class="text-blue-200 text-sm">Infrastructure</div>
        </div>
        <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-700/40 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">Secure</div>
          <div class="text-blue-200 text-sm">By Design</div>
        </div>
      </div>
    </div>
  </div>
</section>
